/**
 * @description
 * Diicaなど、ネイティブのみでアプリを表示するが、アーティスト管理画面をWebでホスティングしたいケースのため、ログインページとconsole下のみルーティングを設定する
 * https://app.clickup.com/t/908127/UT-9697
 */
import dynamic from 'next/dynamic'
import { FC, useEffect, useState } from 'react'
import { Route, Switch } from 'react-router-dom'

import { NotFoundWeb } from '@/components/pages/Errors/NotFoundWeb'
import { useAnalytics } from '@/core/analytics'
import { User, useTrackIdentify } from '@/core/auth'
import { useBackButtonListener } from '@/core/native/app'
import { getRedirectAfterLoginPath } from '@/core/native/storage'
import { useRouter, useTrackPageView } from '@/core/router'
import { handleChunkLoadError } from '@/lib/errorHandler'

import { ConsoleRouter } from './ConsoleRouter'
import { GuestOnlyRoute } from './GuestOnlyRoute'
import { routes } from './routes'

const Login = dynamic<{}>(
  import('@/components/pages/Login')
    .then(m => m.Login)
    .catch(async error => {
      await handleChunkLoadError(error)
      return () => null
    }),
  { ssr: false }
)

type NativeOnlyRouterProps = {
  user: User | null
  isConsole: boolean
}

/**
 * This routing is for Organization App.
 * To build artist stand alone app, we need to create its app router.
 */
export const NativeOnlyRouter: FC<NativeOnlyRouterProps> = ({
  user,
  isConsole,
}) => {
  useBackButtonListener()

  const {
    router: { pathname },
  } = useRouter()

  const [redirectPath, setRedirectPath] = useState<string>(
    routes.GlobalHome.path
  )
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    getRedirectAfterLoginPath()
      .then(async ({ value }) =>
        setRedirectPath(value || routes.ConsoleDashboard.path)
      )
      .finally(() => setLoading(false))

    return () => {}
  }, [])

  const { analytics } = useAnalytics()
  useTrackPageView(({ location }) => {
    /**
     * HACK:
     * SPAのHTMLが切り替わる前にanalytics.pageが発火してしまい前ページの情報が入ってしまうため、0.2秒待つ
     * ref: https://github.com/DavidWells/analytics/issues/177
     */
    setTimeout(() => analytics?.page(), 200)
  })
  useTrackIdentify(async ({ user, applicationKey }) => {
    await analytics?.identify(user, {
      application_key: applicationKey,
    })
  })

  if (loading) return null

  // ログインしているが、アーティストではない場合は404を返す
  if (!!user && !isConsole)
    return <Route path="*" render={() => <NotFoundWeb />} />

  return (
    <Switch>
      <GuestOnlyRoute
        exact
        path={routes.Login.path}
        element={<Login />}
        isLogin={!!user}
        redirectPath={redirectPath}
      />
      {pathname.includes('console') && ConsoleRouter({ user })?.props?.children}
      <Route path="*" render={() => <NotFoundWeb />} />
    </Switch>
  )
}
